import React, { useState, useEffect, useMemo } from "react";
import { Badge, Card, Collapse, Col, Row, Table, Form, InputGroup, Spinner, Pagination, Button } from "react-bootstrap";
import { FaCheck, FaChevronLeft, FaChevronRight, FaFilter, FaTimes } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Select from "react-select";

import { getTicketsAwaitingApproval, approveTickets } from "../../api/TICKET/ticket";
import { useTicketsAwaitingApproval } from "../../hooks/TICKET/useGetTickets";
import { useClients } from "../../hooks/useGetClients";
import { useTicketTypes } from "../../hooks/TICKET/useTicketTypes";
import { useTicketPriorities } from "../../hooks/TICKET/useTicketPriorities";
import { useFacturationModes } from "../../hooks/TICKET/useFacturationModes";
import { useUsers } from "../../hooks/useGetUsers";
import { useGroupes } from "../../hooks/useGetGroupes";
import { customSelectStyles } from "../../utils/reactSelectStyles";
import TicketsApprobationRow from "./TicketsApprobationRow";
import { useAuth } from "../../context/AuthContext";

// ---------------------------------------------------------------------------
// Breakpoint below which the card layout is used instead of the table.
// ---------------------------------------------------------------------------
const MOBILE_BREAKPOINT = 1000;

const OP_OPTIONS = [
  { value: "=",  label: "=" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: ">",  label: ">" },
  { value: "<",  label: "<" },
];

const TicketsApprobation = ({ titre }) => {
  const { hasPermission } = useAuth();

  // ── Router ────────────────────────────────────────────────────────────────
  const location      = useLocation();
  const restoredState = location.state;

  // ── Local UI state ────────────────────────────────────────────────────────
  const [search,           setSearch]           = useState(restoredState?.search || "");
  const [debouncedSearch,  setDebouncedSearch]  = useState(restoredState?.search || "");
  const [page,             setPage]             = useState(restoredState?.page   || 1);
  const [limit,            setLimit]            = useState(restoredState?.limit  || 10);
  const [selectedTickets,  setSelectedTickets]  = useState([]);
  const [isMobile,         setIsMobile]         = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // ── Filters & sort ────────────────────────────────────────────────────────
  const [showFilters,       setShowFilters]       = useState(false);
  const [sortField,         setSortField]         = useState("date_debut");
  const [sortOrder,         setSortOrder]         = useState("DESC");

  const [filterClientInput, setFilterClientInput] = useState("");
  const [filterClient,      setFilterClient]      = useState(null);
  const [filterDescription, setFilterDescription] = useState("");
  const [debouncedDesc,     setDebouncedDesc]     = useState("");
  const [filterType,        setFilterType]        = useState(null);
  const [filterPriorite,    setFilterPriorite]    = useState(null);
  const [filterFacturation, setFilterFacturation] = useState(null);
  const [filterAssignedUsers, setFilterAssignedUsers] = useState([]);
  const [filterAssignedGroups, setFilterAssignedGroups] = useState([]);
  const [filterDateDebut,   setFilterDateDebut]   = useState({ op: ">=", val: "" });
  const [filterDateFin,     setFilterDateFin]     = useState({ op: "<=", val: "" });
  const [filterDateCloture, setFilterDateCloture] = useState({ op: "=",  val: "" });

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === "ASC" ? "DESC" : "ASC");
    else { setSortField(field); setSortOrder("DESC"); }
    setPage(1);
  };

  const resetFilters = () => {
    setFilterClient(null); setFilterClientInput("");
    setFilterDescription(""); setDebouncedDesc("");
    setFilterType(null); setFilterPriorite(null);
    setFilterFacturation(null); setFilterAssignedUsers([]); setFilterAssignedGroups([]);
    setFilterDateDebut({ op: ">=", val: "" });
    setFilterDateFin({ op: "<=", val: "" });
    setFilterDateCloture({ op: "=",  val: "" });
    setPage(1);
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterClient)      n++;
    if (debouncedDesc)     n++;
    if (filterType)        n++;
    if (filterPriorite)    n++;
    if (filterFacturation) n++;
    if (filterAssignedUsers.length > 0) n++;
    if (filterAssignedGroups.length > 0) n++;
    if (filterDateDebut.val)   n++;
    if (filterDateFin.val)     n++;
    if (filterDateCloture.val) n++;
    return n;
  }, [filterClient, debouncedDesc, filterType, filterPriorite, filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture]);

  const apiFilters = useMemo(() => ({
    clientId:    filterClient?.value  ?? null,
    description: debouncedDesc        || "",
    type:        filterType?.value    ?? null,
    priorite:    filterPriorite?.value ?? null,
    facturation: filterFacturation?.value ?? null,
    userIds:     filterAssignedUsers.map(u => u.value),
    groupIds:    filterAssignedGroups.map(g => g.value),
    dateDebut:   filterDateDebut.val  ? filterDateDebut  : null,
    dateFin:     filterDateFin.val    ? filterDateFin    : null,
    dateCloture: filterDateCloture.val ? filterDateCloture : null,
  }), [filterClient, debouncedDesc, filterType, filterPriorite, filterFacturation, filterAssignedUsers, filterAssignedGroups, filterDateDebut, filterDateFin, filterDateCloture]);

  // ── Options data ──────────────────────────────────────────────────────────
  const { data: clientsData }       = useClients(1, 20, filterClientInput.length >= 3 ? filterClientInput : "");
  const { data: typesData }         = useTicketTypes();
  const { data: prioritesData }     = useTicketPriorities();
  const { data: factModesData }     = useFacturationModes();
  const { data: usersData }         = useUsers(1, 100, "");
  const { data: groupesData }       = useGroupes();

  const clientOptions    = useMemo(() => (clientsData?.data?.clients ?? []).map(c => ({ value: c.id, label: c.name })), [clientsData]);
  const typeOptions      = useMemo(() => (typesData?.data ?? []).map(t => ({ value: t.id, label: t.label })), [typesData]);
  const prioriteOptions  = useMemo(() => (prioritesData?.data ?? []).map(p => ({ value: p.id, label: p.label })), [prioritesData]);
  const factOptions      = useMemo(() => (factModesData?.data ?? []).map(f => ({ value: f.id, label: f.label })), [factModesData]);
  const userOptions      = useMemo(() => (usersData?.data?.users ?? usersData?.data ?? []).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` })), [usersData]);
  const groupOptions     = useMemo(() => (groupesData?.data ?? []).map(g => ({ value: g.id, label: g.name })), [groupesData]);

  // ── Remote data & mutations ───────────────────────────────────────────────
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useTicketsAwaitingApproval(
    page, limit, debouncedSearch, apiFilters, sortField, sortOrder
  );

  const approveMutation = useMutation({
    mutationFn: approveTickets,
    onSuccess: () => {
      toast.success(`${selectedTickets.length} ticket(s) approuvé(s) avec succès`);
      setSelectedTickets([]);
      queryClient.invalidateQueries({ queryKey: ["ticketsApproval"] });
      queryClient.invalidateQueries({ queryKey: ["inactiveTickets"] });
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de l'approbation");
    },
  });

  // ── Derived values ────────────────────────────────────────────────────────
  const tickets    = Array.isArray(data?.data?.tickets) ? data.data.tickets : [];
  const totalItems = data?.data?.count || 0;
  const totalPages = Math.ceil(totalItems / limit);

  const formattedTickets = tickets.map(t => ({
    id:                   t.id,
    dateDebut:            t.start_date || t.created_at,
    client:               t.client?.name || "Non assigné",
    titre:                t.title,
    billingMethod:        t.billing_method?.label || "—",
    ticket_interventions: t.ticket_interventions || [],
    usersAssigned: t.users || [],
    groupsAssigned: t.groups || [],
  }));

  const listState = { page, limit, search };

  // ── Effects ───────────────────────────────────────────────────────────────

  // Listen to window resize to switch between card and table layout.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search title
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounce description filter
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDesc(filterDescription), 500);
    return () => clearTimeout(t);
  }, [filterDescription]);

  // Prefetch the next page so navigation feels instant.
  useEffect(() => {
    if (page < totalPages) {
      queryClient.prefetchQuery({
        queryKey: ["ticketsApproval", page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder],
        queryFn:  () => getTicketsAwaitingApproval(page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [page, limit, debouncedSearch, apiFilters, sortField, sortOrder, totalPages, queryClient]);

  // Restore scroll position when the user navigates back from a ticket detail.
  useEffect(() => {
    if (restoredState?.scrollY && !isLoading) {
      window.scrollTo(0, restoredState.scrollY);
    }
  }, [restoredState, isLoading]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value.toString());
    setPage(1);
  };

  // Toggle all tickets on the current page.
  const handleSelectAll = (e) => {
    setSelectedTickets(e.target.checked ? formattedTickets.map(t => t.id) : []);
  };

  // Toggle a single ticket in/out of the selection.
  const handleSelectTicket = (ticketId) => {
    setSelectedTickets(prev =>
      prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]
    );
  };

  const handleApproveSelected = () => {
    if (selectedTickets.length === 0) {
      toast.warning("Veuillez sélectionner au moins un ticket");
      return;
    }
    approveMutation.mutate(selectedTickets);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  // Build a compact page-number array with ellipsis for large page counts.
  const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const delta = 2;
    const left  = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    return [
      1,
      ...(left > 2 ? ["..."] : []),
      ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
      ...(right < total - 1 ? ["..."] : []),
      total,
    ];
  };

  // ── Sub-components ────────────────────────────────────────────────────────

  // Reusable pagination bar — rendered at the top and bottom of the list.
  const paginationBar = (keyPrefix) => (
    <Pagination className="mb-0 flex-wrap">
      <Pagination.Prev
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={page === 1}
      >
        {isMobile ? <FaChevronLeft /> : "Précédent"}
      </Pagination.Prev>

      {getPageNumbers(page, totalPages).map((p, idx) =>
        p === "..." ? (
          <Pagination.Ellipsis key={`${keyPrefix}-ellipsis-${idx}`} disabled />
        ) : (
          <Pagination.Item
            key={`${keyPrefix}-page-${p}`}
            active={p === page}
            onClick={() => setPage(p)}
          >
            {p}
          </Pagination.Item>
        )
      )}

      <Pagination.Next
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
      >
        {isMobile ? <FaChevronRight /> : "Suivant"}
      </Pagination.Next>
    </Pagination>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>

        {selectedTickets.length > 0 && hasPermission("APPROVE_TICKET") && (
          <Button
            variant="success"
            className="ms-2 mb-0"
            onClick={handleApproveSelected}
            disabled={approveMutation.isPending}
          >
            <FaCheck className="me-2" />
            Approuver la sélection ({selectedTickets.length})
          </Button>
        )}
      </div>

      <div className="row">
        <div className="col-12">
          <Card className="border-left-warning shadow mb-4">
            <Card.Body>

              {/* Toolbar: limit + search + filter button + top pagination */}
              <div
                className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"
                style={{ width: "100%" }}
              >
                <div style={{ flex: "1 1 150px", maxWidth: "70px" }}>
                  <Form.Select value={limit} onChange={handleLimitChange} disabled={isLoading}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>

                <div style={{ flex: "1 1 200px", maxWidth: "350px" }}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Recherche par titre"
                      value={search}
                      onChange={handleSearchChange}
                      className="before-input"
                    />
                  </InputGroup>
                </div>

                <Button
                  variant={showFilters || activeFilterCount > 0 ? "warning" : "outline-secondary"}
                  className="d-flex align-items-center gap-2 filter-button"
                  onClick={() => setShowFilters(prev => !prev)}
                >
                  <FaFilter />
                  <span>Filtres</span>
                  {activeFilterCount > 0 && (
                    <Badge bg="light" text="dark" pill className="ms-1" style={{ display: 'inline-flex', alignItems: 'center', height: 'fit-content' }}>{activeFilterCount}</Badge>
                  )}
                </Button>

                <div
                  className="d-flex justify-content-end"
                  style={{ flex: "3 1 250px", minWidth: "250px" }}
                >
                  {paginationBar("top")}
                </div>
              </div>

              {/* Filter panel */}
              <Collapse in={showFilters}>
                <div>
                  <div className="border rounded p-3 mb-3 bg-body-secondary">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-semibold text-muted small text-uppercase">Filtres avancés</span>
                      {activeFilterCount > 0 && (
                        <button className="btn btn-danger btn-icon-split btn-sm" onClick={resetFilters}>
                          <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
                            <FaTimes style={{ color: "#fff" }} />
                          </span>
                          <span className="text">Réinitialiser</span>
                        </button>
                      )}
                    </div>

                    <Row className="g-2 mb-2">
                      {/* Client */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Client</Form.Label>
                        <Select
                          isClearable
                          placeholder="Tous les clients"
                          options={clientOptions}
                          value={filterClient}
                          onInputChange={v => setFilterClientInput(v)}
                          onChange={v => { setFilterClient(v); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                      {/* Type */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Type</Form.Label>
                        <Select
                          isClearable
                          placeholder="Tous les types"
                          options={typeOptions}
                          value={filterType}
                          onChange={v => { setFilterType(v); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                      {/* Priorité */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Priorité</Form.Label>
                        <Select
                          isClearable
                          placeholder="Toutes les priorités"
                          options={prioriteOptions}
                          value={filterPriorite}
                          onChange={v => { setFilterPriorite(v); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                    </Row>

                    <Row className="g-2 mb-2">
                      {/* Facturation */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Facturation</Form.Label>
                        <Select
                          isClearable
                          placeholder="Toutes"
                          options={factOptions}
                          value={filterFacturation}
                          onChange={v => { setFilterFacturation(v); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                      {/* Utilisateur assigne */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Utilisateur assigne</Form.Label>
                        <Select
                          isMulti
                          isClearable
                          placeholder="Tous les utilisateurs"
                          options={userOptions}
                          value={filterAssignedUsers}
                          onChange={v => { setFilterAssignedUsers(v || []); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                      {/* Groupe assigne */}
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Groupe assigne</Form.Label>
                        <Select
                          isMulti
                          isClearable
                          placeholder="Tous les groupes"
                          options={groupOptions}
                          value={filterAssignedGroups}
                          onChange={v => { setFilterAssignedGroups(v || []); setPage(1); }}
                          styles={customSelectStyles}
                        />
                      </Col>
                    </Row>

                    <Row className="g-2 mb-2">
                      {/* Description */}
                      <Col md={12}>
                        <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Filtrer par description..."
                          value={filterDescription}
                          onChange={e => { setFilterDescription(e.target.value); setPage(1); }}
                        />
                      </Col>
                    </Row>

                    {/* Dates */}
                    <Row className="g-2">
                      {[
                        { label: "Date de début",   state: filterDateDebut,   setState: setFilterDateDebut,   defaultOp: ">=" },
                        { label: "Date de fin",     state: filterDateFin,     setState: setFilterDateFin,     defaultOp: "<=" },
                        { label: "Date de clôture", state: filterDateCloture, setState: setFilterDateCloture, defaultOp: "="  },
                      ].map(({ label, state, setState, defaultOp }) => (
                        <Col md={4} key={label}>
                          <Form.Label className="small fw-semibold mb-1">{label}</Form.Label>
                          <InputGroup size="sm">
                            <Form.Select
                              value={state.op}
                              onChange={e => setState(prev => ({ ...prev, op: e.target.value }))}
                              style={{ maxWidth: "60px" }}
                            >
                              {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Select>
                            <Form.Control
                              type="date"
                              value={state.val}
                              onChange={e => { setState(prev => ({ ...prev, val: e.target.value })); setPage(1); }}
                            />
                            {state.val && (
                              <Button variant="outline-secondary" onClick={() => setState({ op: defaultOp, val: "" })}>
                                <FaTimes size={10} />
                              </Button>
                            )}
                          </InputGroup>
                        </Col>
                      ))}
                    </Row>

                  </div>
                </div>
              </Collapse>

              {/* Content area */}
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status" style={{ color: "#f6c23e" }}>
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                </div>

              ) : isError ? (
                <div className="alert alert-danger">Erreur lors du chargement des tickets</div>

              ) : (
                <>
                  {/* Mobile: card grid / Desktop: table */}
                  {isMobile ? (
                    <div className="row g-3">
                      {formattedTickets.length === 0 ? (
                        <div className="col-12">
                          <div className="alert alert-info text-center mb-0">
                            Aucun ticket en attente d'approbation
                          </div>
                        </div>
                      ) : (
                        formattedTickets.map((ticket, idx) => (
                          <div key={ticket.id ?? `ticket-${idx}`} className="col-12">
                            <TicketsApprobationRow
                              ticket={ticket}
                              listState={listState}
                              isSelected={selectedTickets.includes(ticket.id)}
                              onSelectToggle={handleSelectTicket}
                              asCard
                            />
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <Table striped hover responsive className="align-middle">
                      <thead>
                        <tr>
                          {/* Select-all checkbox */}
                          <th style={{ width: "50px" }}>
                            <Form.Check
                              type="checkbox"
                              checked={
                                selectedTickets.length === formattedTickets.length &&
                                formattedTickets.length > 0
                              }
                              onChange={handleSelectAll}
                            />
                          </th>
                          {["id", "titre", "date_debut", "client"].map((field, i) => {
                            const labels = ["ID", "Titre", "Date début", "Client"];
                            return (
                              <th
                                key={field}
                                onClick={() => handleSort(field)}
                                style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                              >
                                {labels[i]}{" "}
                                {sortField === field
                                  ? (sortOrder === "ASC" ? "▲" : "▼")
                                  : <span style={{ opacity: 0.25 }}>▲</span>}
                              </th>
                            );
                          })}
                          <th>Intervenant(s)</th>
                          <th>Intervention(s)</th>
                          <th>Méthode facturation</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formattedTickets.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center">
                              Aucun ticket en attente d'approbation
                            </td>
                          </tr>
                        ) : (
                          formattedTickets.map((ticket, idx) => (
                            <TicketsApprobationRow
                              key={ticket.id ?? `ticket-${idx}`}
                              ticket={ticket}
                              listState={listState}
                              isSelected={selectedTickets.includes(ticket.id)}
                              onSelectToggle={handleSelectTicket}
                            />
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}

                  {/* Bottom pagination + total count */}
                  <div className="d-flex flex-column gap-3 mt-3">
                    {paginationBar("bottom")}
                    <div className="text-muted fst-italic">Total : {totalItems} tickets</div>
                  </div>
                </>
              )}

            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default TicketsApprobation;